import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CropIcon from '@mui/icons-material/Crop';
import * as Cesium from 'cesium';
import ClippingPlanesEditor from '../utils/ClippingPlaneEditor';
import ToolPanel from '../components/ToolPanel';
import TipsWindow from '../components/TipsWindow';
import { useTip } from '../hooks/useTips';
import { FORM_STYLES, SECTION_STYLES, ROW_STYLES, BUTTON_STYLES } from '../styles/viewerStyles';

function ClippingPlaneTools({ viewer, tileset, panelOpen = false, onClosePanel, DRAWER_WIDTH = 285 }) {
  const { t } = useTranslation();
  const [isActive, setIsActive] = useState(false);
  const [editor, setEditor] = useState(null);
  const [collection, setCollection] = useState(null);
  const [target, setTarget] = useState(null);
  // Rotation removed

  // Tips integration
  const { isOpen: isTipOpen, title: tipTitle, markdownBody: tipBody, close: closeTip } = useTip('clipping', panelOpen);

  const findTargetTileset = useCallback(() => {
    if (!viewer) return null;
    if (tileset) return tileset;
    const prims = viewer.scene?.primitives;
    if (!prims) return null;
    for (let i = 0; i < prims.length; i++) {
      const p = prims.get(i);
      if (p && p.constructor && p.constructor.name.includes('Cesium3DTileset')) {
        return p;
      }
    }
    return null;
  }, [viewer, tileset]);

  const createOBBPlanes = useCallback((targetTileset) => {
    try {
      const rootBV = targetTileset?.root?.boundingVolume;
      let obb = null;
      if (rootBV) {
        if (rootBV._orientedBoundingBox) obb = rootBV._orientedBoundingBox;
        else if (rootBV instanceof Cesium.OrientedBoundingBox) obb = rootBV;
      }

      if (!obb) {
        const r = (targetTileset.boundingSphere && targetTileset.boundingSphere.radius) || 200.0;
        return [
          new Cesium.ClippingPlane(new Cesium.Cartesian3(1.0, 0.0, 0.0), -r),
          new Cesium.ClippingPlane(new Cesium.Cartesian3(-1.0, 0.0, 0.0), -r),
          new Cesium.ClippingPlane(new Cesium.Cartesian3(0.0, 1.0, 0.0), -r),
          new Cesium.ClippingPlane(new Cesium.Cartesian3(0.0, -1.0, 0.0), -r),
          new Cesium.ClippingPlane(new Cesium.Cartesian3(0.0, 0.0, 1.0), -r),
          new Cesium.ClippingPlane(new Cesium.Cartesian3(0.0, 0.0, -1.0), -r),
        ];
      }

      const center = obb.center;
      const ha = obb.halfAxes; // Matrix3 in column-major (scaled axes)
      const u = new Cesium.Cartesian3(ha[0], ha[1], ha[2]);
      const v = new Cesium.Cartesian3(ha[3], ha[4], ha[5]);
      const w = new Cesium.Cartesian3(ha[6], ha[7], ha[8]);
      const dirU = Cesium.Cartesian3.normalize(u, new Cesium.Cartesian3());
      const dirV = Cesium.Cartesian3.normalize(v, new Cesium.Cartesian3());
      const dirW = Cesium.Cartesian3.normalize(w, new Cesium.Cartesian3());

      // Expand box by a small margin so planes are slightly outside
      const margin = 0.45; // 25%
      const uExp = Cesium.Cartesian3.multiplyByScalar(u, 1 + margin, new Cesium.Cartesian3());
      const vExp = Cesium.Cartesian3.multiplyByScalar(v, 1 + margin, new Cesium.Cartesian3());
      const wExp = Cesium.Cartesian3.multiplyByScalar(w, 1 + margin, new Cesium.Cartesian3());

      const T = targetTileset.clippingPlanesOriginMatrix;
      const mT = Cesium.Matrix4.transpose(T, new Cesium.Matrix4());
      const toLocalPlane = (nWorld, dWorld) => {
        const planeWorld = new Cesium.Cartesian4(nWorld.x, nWorld.y, nWorld.z, dWorld);
        const planeLocal = Cesium.Matrix4.multiplyByVector(mT, planeWorld, new Cesium.Cartesian4());
        const nLocal = new Cesium.Cartesian3(planeLocal.x, planeLocal.y, planeLocal.z);
        const mag = Cesium.Cartesian3.magnitude(nLocal) || 1.0;
        Cesium.Cartesian3.divideByScalar(nLocal, mag, nLocal);
        const dLocal = planeLocal.w / mag;
        return new Cesium.ClippingPlane(nLocal, dLocal);
      };

      const cPlusU = Cesium.Cartesian3.add(center, uExp, new Cesium.Cartesian3());
      const cMinusU = Cesium.Cartesian3.subtract(center, uExp, new Cesium.Cartesian3());
      const cPlusV = Cesium.Cartesian3.add(center, vExp, new Cesium.Cartesian3());
      const cMinusV = Cesium.Cartesian3.subtract(center, vExp, new Cesium.Cartesian3());
      const cPlusW = Cesium.Cartesian3.add(center, wExp, new Cesium.Cartesian3());
      const cMinusW = Cesium.Cartesian3.subtract(center, wExp, new Cesium.Cartesian3());

      const neg = (vec) => Cesium.Cartesian3.negate(vec, new Cesium.Cartesian3());
      const planesLocal = [];
      // Inverted normals per face
      let nW = neg(dirU); let dWorld = -Cesium.Cartesian3.dot(nW, cPlusU); planesLocal.push(toLocalPlane(nW, dWorld));
      nW = dirU; dWorld = -Cesium.Cartesian3.dot(nW, cMinusU); planesLocal.push(toLocalPlane(nW, dWorld));
      nW = neg(dirV); dWorld = -Cesium.Cartesian3.dot(nW, cPlusV); planesLocal.push(toLocalPlane(nW, dWorld));
      nW = dirV; dWorld = -Cesium.Cartesian3.dot(nW, cMinusV); planesLocal.push(toLocalPlane(nW, dWorld));
      nW = neg(dirW); dWorld = -Cesium.Cartesian3.dot(nW, cPlusW); planesLocal.push(toLocalPlane(nW, dWorld));
      nW = dirW; dWorld = -Cesium.Cartesian3.dot(nW, cMinusW); planesLocal.push(toLocalPlane(nW, dWorld));

      return planesLocal;
    } catch (e) {
      console.warn('Failed OBB planes, fallback to sphere', e);
      const r = (targetTileset.boundingSphere && targetTileset.boundingSphere.radius) || 200.0;
      return [
        new Cesium.ClippingPlane(new Cesium.Cartesian3(1.0, 0.0, 0.0), -r),
        new Cesium.ClippingPlane(new Cesium.Cartesian3(-1.0, 0.0, 0.0), -r),
        new Cesium.ClippingPlane(new Cesium.Cartesian3(0.0, 1.0, 0.0), -r),
        new Cesium.ClippingPlane(new Cesium.Cartesian3(0.0, -1.0, 0.0), -r),
        new Cesium.ClippingPlane(new Cesium.Cartesian3(0.0, 0.0, 1.0), -r),
        new Cesium.ClippingPlane(new Cesium.Cartesian3(0.0, 0.0, -1.0), -r),
      ];
    }
  }, []);

  const toggleClipping = useCallback(() => {
    if (!viewer) return;
    try {
      if (!isActive) {
        const tgt = findTargetTileset();
        if (!tgt) return;
        const planes = createOBBPlanes(tgt);
        const coll = new Cesium.ClippingPlaneCollection({
          planes,
          unionClippingRegions: true,
          edgeWidth: 1.0,
        });
        tgt.clippingPlanes = coll;
        const ed = new ClippingPlanesEditor({
          scene: viewer.scene,
          clippingPlanes: coll,
          movePlanesToOrigin: false,
          pixelSize: new Cesium.Cartesian2(120, 120),
          maximumSizeInMeters: new Cesium.Cartesian2(Infinity, Infinity),
        });
        ed.activate();
        setEditor(ed);
        setCollection(coll);
        setTarget(tgt);
        setIsActive(true);
      } else {
        // detach first, then destroy editor
        try {
          if (collection) collection.enabled = false;
        } catch (e) {
          console.warn('Failed to disable clipping collection:', e);
        }
        if (target && target.clippingPlanes) target.clippingPlanes = undefined;
        if (editor) {
          try { editor.deactivate(); } catch (e) { console.warn('Failed to deactivate editor:', e); }
          try { editor.destroy(); } catch (e) { console.warn('Failed to destroy editor:', e); }
        }
        setEditor(null); setCollection(null); setTarget(null); setIsActive(false);
      }
    } catch (e) {
      console.error('Error toggling clipping', e);
    }
  }, [viewer, isActive, findTargetTileset, createOBBPlanes, collection, target, editor]);

  // No rotation support

  const handleReset = useCallback(() => {
    try {
      editor?.reset();
    } catch (e) {
      console.warn('Failed to reset clipping planes:', e);
    }
  }, [editor]);

  useEffect(() => () => {
    // cleanup on unmount
    try {
      if (collection) collection.enabled = false;
    } catch (e) {
      console.warn('Cleanup: failed to disable collection:', e);
    }
    if (target && target.clippingPlanes) target.clippingPlanes = undefined;
    if (editor) {
      try { editor.deactivate(); } catch (e) { console.warn('Cleanup: failed to deactivate editor:', e); }
      try { editor.destroy(); } catch (e) { console.warn('Cleanup: failed to destroy editor:', e); }
    }
  }, [collection, target, editor]);

  // Keep clipping active when panel closes, but hide visuals and disable handlers
  useEffect(() => {
    if (!editor) return;
    try {
      if (isActive) {
        if (panelOpen) {
          if (!editor.active) editor.activate();
        } else {
          if (editor.active) editor.deactivate();
        }
      }
    } catch (e) {
      console.warn('Failed to toggle editor activation:', e);
    }
  }, [panelOpen, editor, isActive]);

  // Render nothing when panel is closed, but keep component mounted to retain clipping state
  if (!panelOpen) {
    return null;
  }

  return (
    <>
      <TipsWindow
        isOpen={isTipOpen}
        title={tipTitle}
        markdownBody={tipBody}
        onClose={({ doNotShowAgain }) => closeTip(doNotShowAgain)}
      />
      <ToolPanel
        title={t('ClippingPlanes') || 'Clipping Planes'}
        onClose={onClosePanel}
        width={DRAWER_WIDTH}
      >
      <Box sx={ROW_STYLES.buttonRow}>
        {/* Save Current Viewpoint */}
        <ToggleButton
          value="save"
          selected={!!isActive}
          onChange={(e, checked) => { if (checked !== isActive) toggleClipping(); }}
          size="small"
          sx={BUTTON_STYLES.iconButton}
          title={t('Clipping')}
        >
          <CropIcon />
        </ToggleButton>

        {/* Import Viewpoints */}
        <ToggleButton
          value="import"
          onClick={handleReset}
          component="label"
          size="small"
          sx={BUTTON_STYLES.iconButton}
          title={t('ResetClippingPlanes')}
        >
          <RestartAltIcon />
        </ToggleButton>

        {/* Status / Tips */}
      {isActive &&<Box sx={{ px: 1.5,pt:0.25, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1}}>
        <Typography variant="caption" sx={{ color: '#fff' }}>
           {t('ClippingActiveDragPlanes')}
        </Typography>
      </Box>}
      </Box>  
    </ToolPanel>
    </>
  );
}

ClippingPlaneTools.propTypes = {
  viewer: PropTypes.object,
  tileset: PropTypes.object,
  panelOpen: PropTypes.bool,
  onClosePanel: PropTypes.func,
  DRAWER_WIDTH: PropTypes.number,
};

export default ClippingPlaneTools;
